/**
 * Navigation icons, inline.
 *
 * Hand-drawn rather than an icon package: twelve glyphs is not worth a dependency,
 * a bundle and a licence review. All are `aria-hidden` — the label beside them is the
 * accessible name, and an icon that announced itself would double every nav item.
 */

import type { NavIcon } from './navigation';

const paths: Record<NavIcon, string> = {
  dashboard: 'M3 3h7v7H3zM14 3h7v4h-7zM14 10h7v11h-7zM3 13h7v8H3z',
  leads: 'M4 20c0-3.3 2.7-6 6-6s6 2.7 6 6M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M17 8h5M19.5 5.5v5',
  clients: 'M3 20c0-3 2.5-5.5 5.5-5.5S14 17 14 20M8.5 11.5a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5M16 20c0-2.5 1.4-4.4 3.5-5M17.5 11a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5',
  cases: 'M4 7h16v13H4zM9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M4 12h16',
  documents: 'M6 3h7l5 5v13H6zM13 3v5h5M9 13h6M9 17h6',
  tasks: 'M4 6.5l2 2 3.5-3.5M4 13.5l2 2 3.5-3.5M4 20l2-2M13 6h7M13 13h7M13 19h7',
  calendar: 'M4 6h16v15H4zM4 10h16M8 3v4M16 3v4',
  invoices: 'M6 3h12v18l-3-2-3 2-3-2-3 2zM9.5 8h5M9.5 12h5',
  agreements: 'M6 3h8l4 4v9H6zM14 3v4h4M8 20c2-2 3-3 5-3s3 1 5 3',
  conversations: 'M4 5h13v9H8l-4 3zM10 17h6l4 3v-9h-3',
  reports: 'M4 20V9M10 20V4M16 20v-7M22 20H2',
  settings:
    'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M19.4 13a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6h.09A1.65 1.65 0 0 0 10.6 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z',
};

export function NavGlyph({ name, className }: { name: NavIcon; className?: string }) {
  return (
    <svg
      className={className ?? 'h-4 w-4 shrink-0'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name]} />
    </svg>
  );
}
